import React, { useState } from 'react';
import { Button, Card, Checkbox, Form, Input, Spin } from 'antd';
import './styles.css';
import { EyeInvisibleOutlined, EyeTwoTone, LockOutlined, UserOutlined } from '@ant-design/icons';
import { useLoginUserMutation } from '../../../GraphQL/hooks/useLoginUserMutation';
import { LoginInput, TokenResponse } from '../../../shared/types';
import { useNavigate } from 'react-router-dom';
import { useUserState } from '../../../Hooks/useUserState';
import { handleFormChange } from '../../../shared/Utils';
import useToggle from '../../../Hooks/useToggle';
import { notification } from 'antd';
import Meta from 'antd/es/card/Meta';
import ReCAPTCHA from 'react-google-recaptcha';
import styled from 'styled-components';

function LoginForm() {
    const [form] = Form.useForm();
    const [formError, setFormError] = useState(false);
    const [recaptchaState, setRecaptchaState] = useState(false);

    const { setAuth, ...state } = useUserState();

    const { mutation, loading: creating } = useLoginUserMutation();
    const navigate = useNavigate();
    const [check, toggleCheck] = useToggle('persist', false);

    const onFinish = async () => {
        const values = await form.validateFields();

        if (!recaptchaState) {
            notification.error({
                message: 'Captcha required',
                description: 'Please verify that you are not a robot',
            });
            return;
        }

        if (values) {
            await mutation({ email: values.email, password: values.password })
                .then((tokenResponse: TokenResponse) => {
                    setAuth(tokenResponse.accessToken, tokenResponse?.operatorId);
                    navigate('/');
                })
                .finally(() => {
                    notification.success({
                        message: 'Login successful',
                        description: 'You have been logged in successfully.',
                    });
                })
                .catch((err) => {
                    notification.error({
                        message: 'Failed to login',
                        description: 'An error occurred while logging in.',
                    });

                    console.error('Failed to update operator:', err);
                });
        } else {
            notification.error({
                message: 'Form validation failed',
                description: 'Please check the form for errors and try again.',
            });
            console.error('Form validation failed: No values provided');
        }
    };

    return (
        <div
            style={{
                background: '#f0f2f5',
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Card
                style={{
                    width: '500px',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                }}
            >
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <Meta
                        title={<span style={{ fontSize: '28px' }}>Sign In</span>}
                        description="Please enter your Email and password"
                    />
                </div>

                <Spin spinning={creating}>
                    <Form<LoginInput>
                        form={form}
                        name="normal_login"
                        className="login-form"
                        initialValues={{ remember: false }}
                        style={{ textAlign: 'center', width: '100%' }}
                        onFinish={onFinish}
                        onFieldsChange={() => setFormError(handleFormChange(form))}
                        autoComplete="off"
                    >
                        <Form.Item name="email" rules={[{ required: true, message: 'Please input your Username!' }]}>
                            <Input prefix={<UserOutlined className="site-form-item-icon" />} placeholder="Email" />
                        </Form.Item>
                        <Form.Item name="password" rules={[{ required: true, message: 'Please input your Password!' }]}>
                            <Input.Password
                                prefix={<LockOutlined className="site-form-item-icon" />}
                                placeholder="Password"
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>
                        <Form.Item>
                            <RecaptchaWrapper
                                style={{ display: 'flex', justifyContent: 'center', paddingBottom: '20px' }}
                            >
                                <ReCAPTCHA
                                    onChange={() => setRecaptchaState(true)}
                                    onExpired={() => setRecaptchaState(false)}
                                    onErrored={() => setRecaptchaState(false)}
                                    sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                                />
                            </RecaptchaWrapper>
                            <Button
                                type="primary"
                                htmlType="submit"
                                className="login-form-button"
                                disabled={formError || !recaptchaState}
                            >
                                Log in
                            </Button>
                        </Form.Item>
                        <Form.Item>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Form.Item noStyle>
                                    <Checkbox onChange={toggleCheck} checked={check}>
                                        Remember me
                                    </Checkbox>
                                </Form.Item>
                                <a className="login-form-forgot" onClick={() => navigate('/forget')}>
                                    Forgot password
                                </a>
                            </div>
                        </Form.Item>
                    </Form>
                </Spin>
            </Card>
        </div>
    );
}

const RecaptchaWrapper = styled.div`
    @media (max-width: 460px) {
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

export default LoginForm;
